import React from 'react';
import PropTypes from 'prop-types';
import { Box } from 'grommet';
import Mixpanel from '@Helpers/mixpanel';
import { selectEnvironment } from '@Helpers/util';
import ModalRenderer from './ModalRenderer';

const renderContent = (currentYear, bgColor, isAgency) => (
  <Box flex pad={{ horizontal: 'medium' }} background={bgColor}>
    <p>
      Last Modified: April 21,
      {' '}
      {currentYear}
    </p>
    <h1>Terms and Agreements</h1>
    <p>
      This Content Protection Services Agreement (&quot;Agreement&quot;) is entered into between
      Redflag (&quot;Redflag&quot;, &quot;we&quot;, &quot;our&quot;, or &quot;us&quot;)
      and the Customer (&quot;Customer&quot;, &quot;you&quot;, or &quot;your&quot;)
      collectively referred to as the &quot;Parties.&quot;
      {' '}
      By accessing or using Redflag&apos;s YouTube Content ID services or connecting
      any channel to Redflag&apos;s CMS, the Customer agrees to be bound by the
      terms of this Agreement.
    </p>
    <h2>1. Definitions</h2>
    <p>For purposes of this Agreement:</p>
    <ul>
      <li>
        <b>1.1</b>
        {' '}
        &quot;Affiliate CMS&quot; means Redflag&apos;s YouTube Content ID administrative
        environment, made available to Customers for the purpose of managing claims,
        reference files, and monetization.
      </li>
      <li>
        <b>1.2</b>
        {' '}
        &quot;Managed CMS&quot; means a YouTube Content ID CMS with direct management
        permissions. Redflag does not provide access to such a system.
      </li>
      <li>
        <b>1.3</b>
        {' '}
        &quot;Channel&quot; means any YouTube channel owned or controlled by the Customer.
      </li>
      <li>
        <b>1.4</b>
        {' '}
        &quot;YouTube CID&quot; means YouTube&apos;s Content ID system for detecting and
        monetizing copyrighted content.
      </li>
      <li>
        <b>1.5</b>
        {' '}
        &quot;Agreement&quot; means this Agreement, together with all exhibits and order
        forms incorporated herein.
      </li>
      <li>
        <b>1.6</b>
        {' '}
        &quot;Recovered Revenue&quot; means monetization proceeds, Content ID claims
        revenue, or settlement amounts collected by Supplier on behalf of an Enrolled
        Creator.
      </li>
      <li>
        <b>1.7</b>
        {' '}
        &quot;Revenue Share&quot; means the percentage of Recovered Revenue retained by
        Supplier as compensation.
      </li>
      {isAgency && (
        <li>
          <b>1.8</b>
          {' '}
          &quot;Customer&quot; refers to the Agency and all &quot;Sub-Users&quot; or
          &quot;Represented Creators&quot; they bring on board.
        </li>
      )}
    </ul>
    <h2>2. Nature of CMS Access</h2>
    <ul>
      <li>
        2.1 The Customer acknowledges and agrees that Redflag provides access solely to
        an Affiliate CMS.
      </li>
      <li>
        2.2 Redflag does not provide or operate a Managed CMS, and no rights associated
        with a Managed CMS are granted under this Agreement.
      </li>
      {isAgency ? (
        <li>
          2.3 Redflag shall provide Customer with access to the CID Service as described
          in Exhibit A. The CID Service enables Customer to protect, enforce against, and
          where applicable monetize unauthorized use of Enrolled Creators&apos; Content
          across digital Platforms. Customer may administer the CID Service on behalf of
          Enrolled Creators or may provide Enrolled Creators with access to administer
          the service for their own channels, subject to the terms of this Agreement.
        </li>
      ) : (
        <li>
          2.3 CMS access is provided exclusively for YouTube CID administration and may
          not be used for any other purpose.
        </li>
      )}
      {isAgency && (
        <li>
          2.4 CMS access is provided exclusively for YouTube CID administration and may
          not be used for any other purpose.
        </li>
      )}
    </ul>
    <h2>3. Channel Eligibility Requirements</h2>
    <ul>
      <li>
        3.1 To qualify for connection to Redflag&apos;s CMS, a Channel must:
        <ul>
          <li>(a) possess a minimum of 100,000 subscribers;</li>
          <li>
            (b) be pre-approved for monetization under the YouTube Partner Program (YPP);
            and
          </li>
          <li>
            (c) be in good standing with YouTube, with no unresolved policy violations or
            restrictions.
          </li>
        </ul>
      </li>
      <li>
        3.2 Redflag may reject or disconnect any Channel that fails to meet these
        eligibility requirements, at its sole discretion.
      </li>
    </ul>
    <h2>4. CMS Access Restrictions</h2>
    <ul>
      <li>
        4.1 The Redflag CMS does not permit users to create new YouTube channels. Only
        existing Channels may be connected.
      </li>
      <li>
        4.2 Redflag retains exclusive administrative control and may revoke or modify
        Channel access with or without notice where deemed necessary to protect the CMS,
        Redflag, or YouTube&apos;s systems.
      </li>
      <li>
        4.3 Customer shall not attempt to circumvent or modify CMS limitations, nor
        attempt to access features not expressly granted under this Agreement.
      </li>
    </ul>
    <h2>5. Submission of Reference Files</h2>
    <ul>
      <li>
        5.1 The Customer shall submit only content for which the Customer holds full
        ownership and all rights necessary to authorize YouTube CID administration.
      </li>
      <li>
        5.2 Customer represents and warrants that all submitted content and metadata are
        accurate, complete, and lawful.
        5.2 Customer represents and warrants that all submitted content and metadata are
        accurate, complete, and lawful.
      </li>
      <li>
        5.3 Redflag may, at its discretion, reject reference files that are inaccurate,
        low-quality, high-risk, or legally questionable.
        5.3 Redflag may, at its discretion, reject reference files that are inaccurate,
        low-quality, high-risk, or legally questionable.
      </li>
    </ul>
    <h2>6. Revenue Share and Payment Terms</h2>
    <ul>
      <li>6.1 Customer agrees to pricing terms listed in Exhibit A.</li>
      <li>
        6.2 Redflag shall distribute the Customer&apos;s Revenue Share for YouTube CID
        earnings within 45 days of receipt.
        6.2 Redflag shall distribute the Customer&apos;s Revenue Share for YouTube CID
        earnings within 45 days of receipt.
      </li>
      <li>
        6.3 All revenue calculations are based solely on YouTube&apos;s official
        6.3 All revenue calculations are based solely on YouTube&apos;s official
        system-generated CID reports. Redflag does not modify or recalculate earnings.
      </li>
      <li>
        6.4 Redflag shall not be liable for delays, discrepancies, or reporting issues
        originating from YouTube or third-party systems.
      </li>
      <li>
        6.5 Customer must provide accurate and up-to-date payment information. Redflag
        may withhold payments until correct information is provided.
      </li>
      {isAgency && (
        <li>
          6.6 Where agreed in a separate Order Form or written amendment, Supplier may
          provide Agency with an Agency Share drawn from Supplier&apos;s Revenue Share.
          The Agency Share, if any, shall be:
          <ul>
            <li>
              (a) specified in the applicable Order Form as a percentage of
              Supplier&apos;s Revenue Share;
            </li>
            <li>
              (b) paid to Agency on the same schedule as Creator payouts under Section
              3.2; and
            </li>
            <li>
              (c) reported separately from Creator payouts in Supplier&apos;s reporting
              dashboard. For the avoidance of doubt, Agency Share reduces only
              Supplier&apos;s Revenue Share and does not reduce the amount payable to
              Enrolled Creators.
            </li>
          </ul>
        </li>
      )}
      <li>
        {isAgency ? '6.7' : '6.6'}
        {' '}
        If the Customer has opted for Content ID (CID) services, they are required to
        generate a minimum of $500.00 (USD) in gross revenue within the first thirty
        (30) days following service activation. Failure to meet this revenue threshold
        will result in automatic disqualification from the CID program. Upon
        disqualification, the Customer&apos;s assets and/or Channel will be removed
        from the Redflag system within five (5) business days.
      </li>
    </ul>
    <h2>7. Revenue Payout Schedule</h2>
    <p>
      After applying the agreed revenue split, Redflag transfers the remaining balance
      to the Customer according to the following timeline:
    </p>
    <ul>
      <li>
        Between the 7th&ndash;12th of the month: Earnings are finalized and posted in
        Redflag&apos;s AdSense for YouTube.
      </li>
      <li>
        Between the 21st&ndash;26th of the month: Payments are issued into
        Redflag&apos;s connected account.
      </li>
      <li>
        Between the 27th&ndash;30th of the month: Redflag pays out revenue shares to
        partners.
      </li>
    </ul>
    <h2>
      8. YouTube Content ID Channel Linking Method &ndash; Agreed Setup
    </h2>
    <p>
      The Customer acknowledges and agrees to proceed with the Manual Asset Upload Only
      setup under Redflag&apos;s Affiliate CMS.
    </p>
    <p>
      <b>Scope of Integration:</b>
    </p>
    <ul>
      <li>Manual asset upload only (no channel linking and no AdSense linking).</li>
      <li>Content protection through YouTube Content ID.</li>
      <li>Whitelist enabled for approved channels or partners.</li>
      <li>
        Ability to choose between monetization or takedown actions when matches are
        detected.
      </li>
    </ul>
    <p>
      <b>How it works:</b>
    </p>
    <ul>
      <li>
        The Customer provides owned content files for manual fingerprinting inside
        Redflag&apos;s CMS.
      </li>
      <li>YouTube identifies matching uploads across its platform.</li>
      <li>
        Redflag manages claims according to the selected action (monetize or takedown).
      </li>
      <li>
        Monetization revenue from re-uploads is processed under the agreed revenue share
        terms.
      </li>
    </ul>
    <p>
      By entering into this Agreement, the Customer confirms acceptance of the Manual
      Asset Upload Only method as the official Content ID integration approach.
    </p>
    <h2>9. Copyright Strikes and Compliance</h2>
    <ul>
      <li>
        <b>9.1 Notice and Response Requirements</b>
        <ul>
          <li>
            9.1.1 If a connected Channel receives a YouTube Copyright Strike, the
            Customer must:
            <ul>
              <li>(a) respond to the strike within forty-eight (48) hours; and</li>
              <li>(b) notify Redflag promptly of the response and status.</li>
            </ul>
          </li>
          <li>
            9.1.2 Failure to comply grants Redflag the right to remove the Channel from
            the CMS immediately.
          </li>
        </ul>
      </li>
      <li>
        <b>9.2 Multiple Copyright Strikes</b>
        <ul>
          <li>
            9.2.1 If a Channel receives more than one (1) active Copyright Strike,
            Redflag may remove the Channel from its CMS without prior notice.
          </li>
          <li>
            9.2.2 Redflag shall not be liable for any loss of revenue, monetization, or
            channel access resulting from YouTube enforcement actions.
          </li>
        </ul>
      </li>
    </ul>
    <h2>10. Representations and Warranties</h2>
    {isAgency ? (
      <ul>
        <li>
          <b>10.1 Mutual Representations.</b>
          {' '}
          Each party represents and warrants that: (a) it has the full authority to
          enter into and perform this Agreement; (b) its performance does not breach any
          other agreement to which it is a party; and (c) it will comply with all
          applicable laws in connection with its obligations under this Agreement.
        </li>
        <li>
          <b>10.2 Disclaimer.</b>
          {' '}
          EXCEPT AS EXPRESSLY SET FORTH IN THIS AGREEMENT, THE CID SERVICE IS PROVIDED
          ON AN AS-IS BASIS. SUPPLIER MAKES NO WARRANTIES, EXPRESS OR IMPLIED, INCLUDING
          WITHOUT LIMITATION WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
          PURPOSE, OR NON-INFRINGEMENT. SUPPLIER DOES NOT WARRANT THAT THE CID SERVICE
          WILL BE UNINTERRUPTED, ERROR-FREE, OR THAT ANY SPECIFIC LEVEL OF RECOVERED
          REVENUE WILL BE ACHIEVED.
        </li>
      </ul>
    ) : (
      <p>
        <b>Disclaimer.</b>
        {' '}
        EXCEPT AS EXPRESSLY SET FORTH IN THIS AGREEMENT, THE CID SERVICE IS PROVIDED ON
        AN AS-IS BASIS. SUPPLIER MAKES NO WARRANTIES, EXPRESS OR IMPLIED, INCLUDING
        WITHOUT LIMITATION WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
        PURPOSE, OR NON-INFRINGEMENT. SUPPLIER DOES NOT WARRANT THAT THE CID SERVICE
        WILL BE UNINTERRUPTED, ERROR-FREE, OR THAT ANY SPECIFIC LEVEL OF RECOVERED
        REVENUE WILL BE ACHIEVED.
      </p>
    )}
    <h2>11. Termination</h2>
    <ul>
      <li>
        10.1 Redflag may suspend or terminate this Agreement, or revoke CMS access in
        whole or in part, at its sole discretion if:
        <ul>
          <li>(a) the Customer violates any provision of this Agreement;</li>
          <li>(b) the Customer submits unauthorized or high-risk content;</li>
          <li>(c) the Channel receives strikes or enforcement actions;</li>
          <li>(d) required account or payment information is not provided; or</li>
          <li>
            (e) the Customer engages in fraudulent, abusive, or noncompliant conduct.
          </li>
        </ul>
      </li>
      <li>
        10.2 Either Party may terminate this Agreement for any reason by providing at
        least thirty (30) days&apos; prior written notice to the other Party. The
        Agreement shall remain in full effect until the conclusion of the 30-day notice
        period.
      </li>
      <li>
        10.3 Upon termination, all active claims and reference files associated with the
        Customer will be removed, and all CID operations will cease immediately.
      </li>
    </ul>
    <h2>12. Indemnification</h2>
    <p>
      The Customer agrees to indemnify, defend, and hold harmless Redflag, its officers,
      employees, agents, and affiliates from and against any and all claims, damages,
      liabilities, penalties, losses, or expenses (including reasonable attorneys&apos;
      fees) arising out of or related to:
    </p>
    <ul>
      <li>(a) Customer-submitted content;</li>
      <li>(b) ownership or copyright disputes;</li>
      <li>(c) misuse of the CMS;</li>
      <li>(d) violations of YouTube policies; or</li>
      <li>(e) breaches of this Agreement.</li>
    </ul>
    <h2>13. Limitation of Liability</h2>
    <ul>
      <li>
        12.1 Redflag shall not be liable for:
        12.1 Redflag shall not be liable for:
        <ul>
          <li>
            (a) YouTube platform decisions, including demonetization, removals,
            takedowns, or enforcement actions;
          </li>
          <li>(b) revenue discrepancies or reporting delays;</li>
          <li>(c) indirect, incidental, or consequential damages; or</li>
          <li>(d) loss of profits, channel access, or business opportunities.</li>
        </ul>
      </li>
      <li>
        12.2 Redflag&apos;s total liability under this Agreement shall not exceed the
        total Revenue Share paid to the Customer within the preceding three (3) months.
      </li>
    </ul>
    <h2>14. Amendments</h2>
    <p>
      Redflag may modify or update this Agreement at any time. Continued use of the CMS
      constitutes acceptance of the revised terms.
    </p>
    <h2>15. Governing Law</h2>
    <p>
      This Agreement shall be governed by and interpreted in accordance with the laws of
      the jurisdiction in which Redflag conducts business, unless otherwise mandated by
      applicable law.
    </p>
    <h2>16. Data Privacy and GDPR Compliance</h2>
    <ul>
      <li>
        <b>16.1 Compliance with Applicable Law</b>
        <br />
        Redflag shall process all personal data in accordance with applicable data
        protection laws, including, where relevant, the EU General Data Protection
        Regulation (GDPR), the UK GDPR, and any equivalent local data protection
        regulations.
      </li>
      <li>
        <b>16.2 Categories of Data Processed</b>
        <br />
        In connection with CMS administration, Redflag may process the following personal
        data related to the Customer or the Customer&apos;s authorized representatives:
        <ul>
          <li>Name and contact details</li>
          <li>Email addresses</li>
          <li>Payment information</li>
          <li>Channel ownership and verification information</li>
          <li>Account credentials applicable to YouTube CMS access (if provided)</li>
          <li>Correspondence related to claims, strikes, or compliance matters</li>
        </ul>
        Redflag does not process or require sensitive personal data.
      </li>
      <li>
        <b>16.3 Purpose of Processing</b>
        <br />
        Redflag processes personal data solely for the following purposes:
        <ul>
          <li>Verifying channel eligibility and ownership</li>
          <li>Managing YouTube CID claims and reference files</li>
          <li>Communicating regarding strikes, compliance, or CMS administration</li>
          <li>Issuing payments and accounting</li>
          <li>Fraud detection, risk management, and platform security</li>
        </ul>
        Personal data will not be used for unrelated or unauthorized purposes.
      </li>
      <li>
        <b>16.4 Legal Basis for Processing (GDPR)</b>
        <br />
        Where GDPR applies, Redflag processes personal data under the following legal
        bases:
        <ul>
          <li>Performance of this Agreement (Art. 6(1)(b))</li>
          <li>
            Legitimate interests in fraud prevention, platform integrity, and account
            management (Art. 6(1)(f))
          </li>
          <li>Compliance with legal obligations (Art. 6(1)(c))</li>
          <li>Consent, where expressly required (Art. 6(1)(a))</li>
        </ul>
      </li>
      <li>
        <b>16.5 Data Storage and International Transfers</b>
        <br />
        Personal data may be stored or processed in jurisdictions outside the
        Customer&apos;s country of residence.
        <br />
        Redflag shall ensure that such transfers occur only where:
        <ul>
          <li>Adequate protection levels exist, or</li>
          <li>
            Appropriate safeguards (such as Standard Contractual Clauses) are in place.
          </li>
        </ul>
      </li>
      <li>
        <b>16.6 Data Security</b>
        <br />
        Redflag implements commercially reasonable technical and organizational measures
        to protect personal data against:
        <ul>
          <li>Unauthorized access</li>
          <li>Loss, misuse, alteration, or disclosure</li>
          <li>Security breaches or cyberattacks</li>
        </ul>
        However, no data transmission system is entirely secure, and Redflag disclaims
        liability for breaches outside its reasonable control.
      </li>
      <li>
        <b>16.7 Data Retention</b>
        <br />
        Personal data will be retained only for as long as necessary to fulfill the
        purposes described in this Agreement or as required by applicable law, accounting,
        or regulatory obligations.
      </li>
      <li>
        <b>16.8 Customer Rights Under GDPR (where applicable)</b>
        <br />
        Where GDPR applies, the Customer has the following rights:
        <ul>
          <li>Right to access personal data</li>
          <li>Right to rectify inaccurate data</li>
          <li>
            Right to request erasure (&quot;right to be forgotten&quot;)
          </li>
          <li>Right to restrict processing</li>
          <li>Right to data portability</li>
          <li>Right to object to certain processing activities</li>
          <li>Right to lodge a complaint with a supervisory authority</li>
        </ul>
        Requests may be submitted to Redflag in writing. Redflag will respond within
        legally required timeframes.
      </li>
      <li>
        <b>16.9 Subprocessors</b>
        <br />
        Redflag may engage third-party service providers (e.g., payment processors,
        analytics services, hosting providers) as subprocessors. Redflag ensures all
        subprocessors are bound by data protection obligations consistent with this
        Agreement.
      </li>
      <li>
        <b>16.10 No Sale of Personal Data</b>
        <br />
        Redflag does not sell, rent, or trade personal data to third parties.
      </li>
    </ul>
    <h2>17. Agreement Term and Renewal</h2>
    <p>
      This Agreement shall begin on the Effective Date listed in Exhibit A and will
      remain in force for an initial one (1) month term.
    </p>
    <p>
      After completion of the initial 1-month period, the Agreement may be renewed by
      mutual written agreement of both Parties, provided that both Redflag and the
      Customer are satisfied with the collaboration and performance under this Agreement.
    </p>
    <h2>18. Entire Agreement</h2>
    <p>
      This Agreement constitutes the entire understanding between the Parties and
      supersedes all prior discussions, proposals, or agreements relating to the subject
      matter herein.
    </p>
    <p>
      <b>
        By checking the box below, the Customer acknowledges that they have read,
        understood, and agree to be bound by the full Content Protection Services Agreement,
        including all Terms, Conditions, Policies, and Data Privacy/GDPR provisions.
      </b>
    </p>
    <br />
  </Box>
);

const CidOnboardingTermsModal = ({
  toggle,
  isSmall,
  currentYear,
  background = 'var(--page-modal-bg)',
  textColor = 'var(--page-modal-text)',
  isAgency = false,
}) => {
  React.useEffect(() => {
    Mixpanel.track('modal_view', {
      modal: isAgency
        ? 'Redflag\'s Content Protection Services Agreement (Agency)'
        : 'Redflag\'s Content Protection Services Agreement',
      environment: selectEnvironment(),
    });
  }, []);

  return (
    <ModalRenderer
      toggle={toggle}
      isSmall={isSmall}
      title="Redflag's Content Protection Services Agreement"
      renderContent={() => renderContent(currentYear, background, isAgency)}
      background={background}
      textColor={textColor}
    />
  );
};

CidOnboardingTermsModal.propTypes = {
  toggle: PropTypes.func.isRequired,
  isSmall: PropTypes.bool.isRequired,
  currentYear: PropTypes.number.isRequired,
  background: PropTypes.string,
  textColor: PropTypes.string,
  isAgency: PropTypes.bool,
};

export default CidOnboardingTermsModal;
